import React from 'react';
import PropTypes from 'prop-types';

import DefaultForm from '../../formComponents/DefaultForm';
import RecipientSignupQuestions from './RecipientSignupQuestions';

import { ORG_TYPES } from '../../common/constants';
import { addErrorTags, validateEmail, isValidPhoneNumber } from '../../helpers/validationHelpers';
import DefaultInput from '../../formComponents/DefaultInput';
import PhoneInput from '../../formComponents/PhoneInput';

/**
 * Component for Business, Recipient, and Household Signup
 * @prop save - callback function to save business data to parent
 * @prop userType - either "business" or "recipient"
 */
export default class SignupForm extends DefaultForm {
  constructor(props) {
    super(props);
    this.state = {
      agreeTOS: false,
      receive_newsletter: true,
      errorList: [],
      email: props.user && props.user.email,  // fill in email when we're editing existing user
      ...(props.initialData || {}), // Spread the initialData if it exists
    };
  }

  setValidNPO = (e) => {
    this.setState({ isValidNPO: e.target.value === 'yes' });
  };

  setValidFoodHandler = (e) => {
    this.setState({ isValidFoodHandler: e.target.value === 'yes' });
  };

  setValidRefrigerator = (e) => {
    this.setState({ isValidRefrigerator: e.target.value === 'yes' });
  };

  // takes an object of the form { inputName, errorString }
  addErrorToErrorList = (error) => {
    const { errorList } = this.state;
    errorList.push(error);
    this.setState({ errorList });
  };

  resetPreviousErrors = () => {
    this.state.errorList.forEach((error) => {
      const el = document.getElementsByName(error.inputName)[0];
      el.classList.remove('red-border');

      const errorMsgEl = el.parentNode.getElementsByClassName('error-msg')[0];
      el.parentNode.removeChild(errorMsgEl);
    });
  };

  toggleTermsTOS = () => {
    this.setState({
      agreeTOS: !this.state.agreeTOS,
    });
  };

  toggleTermsNewsletter = () => {
    this.setState({
      receive_newsletter: !this.state.receive_newsletter,
    });
  };

  validateInputsNotBlank = () => {
    const {
      name, // for user permissioning
      first_name: firstName,
      last_name: lastName,
      phone,
      email,
      password,
      password_confirmation: passwordConfirmation,
      agreeTOS,
    } = this.state;
    return (
      (name || this.props.user) // existing users don't fill out this field
      && firstName
      && lastName
      && phone
      && email
      && password
      && passwordConfirmation
      && agreeTOS
    );
  };

  validatePassword = (pwd, pwdConfirm) => {
    const letters = /[A-Za-z]+/;
    const numbers = /[0-9]+/;
    const symbols = /[!"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/;

    if (pwd !== pwdConfirm) {
      this.addErrorToErrorList(
        {
          inputName: 'password_confirmation',
          errorString: 'Passwords do not match',
        },
      );
    }

    if (pwd.length < 8) {
      this.addErrorToErrorList(
        { inputName: 'password', errorString: 'Password is too short' },
      );
    } else if (!letters.test(pwd) || !numbers.test(pwd) || !symbols.test(pwd)) {
      this.addErrorToErrorList(
        {
          inputName: 'password',
          errorString: 'Please choose a stronger password. Use a mix of letters, numbers, and symbols.',
        },
      );
    }
  };

  saveUserData = () => {
    const { orgType, save } = this.props;

    // eslint-disable-next-line no-underscore-dangle
    const data = this._formFields();
    delete data.errorList; // don't send errorList to the API
    this.resetPreviousErrors();

    // Clear the error list from possible previous checks
    this.state.errorList = [];

    // validate email
    if (!validateEmail(data.email)) {
      this.addErrorToErrorList({
        inputName: 'email',
        errorString: 'Please enter a valid email.',
      });
    }

    if (!isValidPhoneNumber(data.phone)) {
      this.addErrorToErrorList({
        inputName: 'phone',
        errorString: 'Please enter a valid phone number.',
      });
    }

    // validate password
    this.validatePassword(data.password, data.password_confirmation);

    if (this.state.errorList.length > 0) {
      addErrorTags(this.state.errorList);
    } else {
      // No errors, go ahead and save
      // Make sure phone has no dashes
      data.phone = data.phone.split('-').join('');

      if (orgType === ORG_TYPES.BUSINESS) {
        data.type = 'BusinessOrganization';
      } else if (orgType === ORG_TYPES.RECIPIENT) {
        data.type = 'RecipientOrganization';
      }

      if (data.recipient_user_id === '0') {
        data.recipient_user_id = null;
      }
      save(data);
    }
  };

  render() {
    const { orgType, user } = this.props;

    const isCreatingNew = user == null;

    const {
      isValidNPO,
      isValidFoodHandler,
      isValidRefrigerator,
    } = this.state;

    const isValid = this.validateInputsNotBlank();

    const isValidOrganization = !(orgType === ORG_TYPES.RECIPIENT)
      || (isValidNPO && isValidFoodHandler && isValidRefrigerator)
      || !isCreatingNew;

    let tosAlert;
    if (this.state.tosAlert) {
      tosAlert = (
        <p className="text-danger mt-1">
          You must agree to the terms of service!
        </p>
      );
    }

    const organizationDescriptor = orgType === ORG_TYPES.BUSINESS ? 'Company' : 'Organization';
    let ftpiElement;

    if (orgType === ORG_TYPES.BUSINESS) {
      ftpiElement = (
        <span>
          and have read the
          {' '}
          <a href="/instructions.pdf" target="_blank">
            {' '}
            First
            Time Pickup Instructions
          </a>
        </span>
      );
    }

    const newsletterText = 'Sign me up to receive the Replate Newsletter so '
        + 'I can always know the latest in global sustainability, climate, '
        + 'and food waste news!';

    const organizationName = 'name';

    let checkboxClassName = 'form-check-input';
    let textInputClass = '';
    if (orgType !== ORG_TYPES.BUSINESS) {
      checkboxClassName += ' rp-recipient';
      textInputClass = 'rp-recipient';
    }

    const submitButtonClass = orgType === ORG_TYPES.BUSINESS ? 'btn-primary' : 'btn-recipient';

    return (
      <div className={`signup-form ${orgType === ORG_TYPES.BUSINESS}`}>
        <div className="signup-section-title">
          <div className="title-name-row">
            <h3>
              { isCreatingNew ? 'Create Your Account' : 'Finish Creating Your Account' }
            </h3>
          </div>
        </div>

        { (orgType === ORG_TYPES.RECIPIENT && isCreatingNew) && (
          <RecipientSignupQuestions
            setValidNPO={this.setValidNPO}
            setValidFoodHandler={this.setValidFoodHandler}
            setValidRefrigerator={this.setValidRefrigerator}
            isValidNPO={this.state.isValidNPO}
            isValidFoodHandler={this.state.isValidFoodHandler}
            isValidRefrigerator={this.state.isValidRefrigerator}
          />
        )}

        { isValidOrganization ? (
          <div className="mt-3">
            {
              orgType === ORG_TYPES.RECIPIENT && (
                <legend className="label fs-6 mb-1">
                  Please note that signing up does not guarantee food donations. 
                  Replate matches donations to Recipient Organizations based on location and availability.
                </legend>
              )
            }
            {
              isCreatingNew && (
              <DefaultInput
                label={`${organizationDescriptor} Name`}
                name={organizationName}
                id="organization_name"
                inputClass={textInputClass}
                type="text"
            // eslint-disable-next-line no-underscore-dangle
                onChange={this._handleChange}
                value={this.state[organizationName] || ''}
              />
              )
            }

            <DefaultInput
              label="First Name"
              name="first_name"
              id="first_name"
              inputClass={textInputClass}
              type="text"
            // eslint-disable-next-line no-underscore-dangle
              onChange={this._handleChange}
              value={this.state.first_name || ''}
            />

            <DefaultInput
              label="Last Name"
              name="last_name"
              id="last_name"
              inputClass={textInputClass}
              type="text"
            // eslint-disable-next-line no-underscore-dangle
              onChange={this._handleChange}
              value={this.state.last_name || ''}
            />

            <PhoneInput
              form_name="phone"
              input_id="phone"
              inputClass={textInputClass}
            // eslint-disable-next-line no-underscore-dangle
              change={this._handleChange}
              helpText="For non US phone numbers, please lead with '+' followed
              by country code"
              value={this.state.phone || ''}
            />

            <DefaultInput
              label="Email"
              name="email"
              id="email"
              type="email"
              inputClass={textInputClass}
            // eslint-disable-next-line no-underscore-dangle
              onChange={this._handleChange}
              readonly={!isCreatingNew}
              value={this.state.email || ''}
            />

            <DefaultInput
              label="Password"
              name="password"
              id="password"
              type="password"
              inputClass={textInputClass}
            // eslint-disable-next-line no-underscore-dangle
              onChange={this._handleChange}
              helpText="Password must be at least 8 characters long and contain at least one letter, one number, and one symbol."
              value={this.state.password || ''}
            />

            <DefaultInput
              label="Confirm Password"
              name="password_confirmation"
              id="password_confirmation"
              type="password"
              inputClass={textInputClass}
            // eslint-disable-next-line no-underscore-dangle
              onChange={this._handleChange}
              value={this.state.password_confirmation || ''}
            />

            <div className="mt-2">
              <div className="form-check">
                <input
                  type="checkbox"
                  id="tos-agree"
                  className={checkboxClassName}
                  checked={this.state.agreeTOS}
                  onChange={this.toggleTermsTOS}
                />
                <label htmlFor="tos-agree" className="tos-agree form-check-label">
                  I agree to the Replate
                  {' '}
                  <a href="/terms" target="_blank">
                    Terms
                    of Use and Privacy Policy
                  </a>
                  {' '}
                  {ftpiElement}
                </label>
              </div>
              {tosAlert}
              <br />
              <div className="form-check">
                <input
                  type="checkbox"
                  id="newsletter-agree"
                  className={checkboxClassName}
                  checked={this.state.receive_newsletter}
                  onChange={this.toggleTermsNewsletter}
                />
                <label
                  htmlFor="newsletter-agree"
                  className="newsletter-agree form-check-label"
                >
                  {newsletterText}
                </label>
              </div>
            </div>
            <div className="mobile-disclaimer">
              We use your mobile number to send delivery updates and contact you.
            </div>

            <div className={`my-4 submit-button-container
              ${this.props.userType === 'business'}`}
            >
              <a
                href="/"
                className="button button--text-alert me-1"
              >
                Cancel
              </a>
              <button
                type="submit"
                className={`btn btn-lg float-end ${submitButtonClass}
                      ${!isValid ? 'button--disabled' : ''}`}
                disabled={!isValid}
                onClick={this.saveUserData}
              >
                Continue
                <span className="fa fa-angle-right ms-1" />
              </button>
            </div>
          </div>
        ) : null }

      </div>
    );
  }
}

SignupForm.propTypes = {
  save: PropTypes.func.isRequired,
  orgType: PropTypes.oneOf(Object.values(ORG_TYPES)).isRequired,
  initialData: PropTypes.object,
};
